import { genericHungLight } from '@shared-lib/lighting/genericCeilingLights';
/**
 * LIGHTING — proportions shared by `lightingRenderers.js` (3D) and the
 * `WallSconceSchematic` / `VanityLightSchematic` / `CeilingLightSchematic`
 * elevation drawings (2D). Re-exported from `proceduralSpecs.js`; split out
 * so that file stays under its size ceiling.
 *
 * PURE (no Three.js, no React). Units are METERS.
 *
 * Every `*Proportions(dims)` function returns ONE layout in a frame both
 * sides can map trivially:
 *   - x: centred on the object (0 = middle of the width)
 *   - y: measured UP from the bottom of the declared box (0 = box bottom)
 *   - z: measured from the WALL plane INTO the room (0 = wall face), for the
 *        two wall-mounted fixtures. The ceiling light is radially symmetric
 *        and only needs radii + a vertical stack.
 * The 3D renderer subtracts height/2 and depth/2 (its frame is bbox-centred;
 * the wall sits at local z = -depth/2, the ceiling at local y = +height/2,
 * per `object-alignment-config.js` verticalAlignment 'top' and
 * SceneObject's ceiling-light top-anchoring). The schematic flips y
 * (SVG y grows downward) and, in a side view, uses z as its x.
 */

export const IN_TO_M = 0.0254;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// ---------------------------------------------------------------------------
// WALL SCONCE (`wall-light`) — uplight cone sconce
// ---------------------------------------------------------------------------
// A rectangular backplate sized like a standard single-gang / octagon
// junction-box cover (4.5" square, NEC 314 box covers are 4" or 4-11/16"),
// a short horizontal arm and vertical stem, and an open frosted cone shade
// whose wide mouth faces UP with its rim flush to the top of the declared
// box. Default catalogue box is 6"w x 12"h x 5"d (`defaultDimensions.js`);
// the vanity pipeline's fallback sconce is 4" x 12" x 4"
// (`addVanityLight.js` DEFAULT_DIMS.sconce).
export const WALL_SCONCE = {
  backplateSide: 4.5 * IN_TO_M, // junction-box cover, clamped to the box
  backplateWidthFrac: 0.75, // never wider than 3/4 of the declared width
  backplateHeightFrac: 0.4, // never taller than 40% of the declared height
  backplateDepth: 0.75 * IN_TO_M,
  backplateDepthFrac: 0.2, // of depth, for very shallow boxes
  shadeTopRadiusFrac: 0.5, // cone mouth fills the width, capped by the depth
  shadeWallClearance: 0.25 * IN_TO_M, // rim stays this far off the wall paint
  shadeHeightFrac: 0.5, // of height, clamped 4"..7"
  shadeHeightMin: 4 * IN_TO_M,
  shadeHeightMax: 7 * IN_TO_M,
  shadeBottomRadiusFrac: 0.4, // of the mouth radius, clamped 0.6"..1.25"
  shadeBottomRadiusMin: 0.6 * IN_TO_M,
  shadeBottomRadiusMax: 1.25 * IN_TO_M,
  socketHeight: 0.5 * IN_TO_M, // metal cup where the stem enters the shade
  armRadius: 0.2 * IN_TO_M,
  bulbRadius: 0.6 * IN_TO_M, // candelabra (E12) lamp envelope, not a globe
};

export function wallSconceProportions({ width, height, depth }) {
  const s = WALL_SCONCE;
  const backplateW = Math.min(s.backplateSide, width * s.backplateWidthFrac);
  const backplateH = Math.min(s.backplateSide, height * s.backplateHeightFrac);
  const backplateD = Math.min(s.backplateDepth, depth * s.backplateDepthFrac);
  // The cone mouth is the widest part. It fills the width, but its rim must
  // also stay between the wall and the front of the box: the shade axis sits
  // at z = depth - r, so r <= (depth - clearance)/2 keeps the back of the rim
  // just off the wall.
  const shadeTopR = Math.min(
    width * s.shadeTopRadiusFrac,
    (depth - s.shadeWallClearance) / 2
  );
  const shadeBottomR = clamp(
    shadeTopR * s.shadeBottomRadiusFrac,
    s.shadeBottomRadiusMin,
    Math.min(s.shadeBottomRadiusMax, shadeTopR)
  );
  const shadeH = clamp(
    height * s.shadeHeightFrac,
    Math.min(s.shadeHeightMin, height),
    Math.min(s.shadeHeightMax, height)
  );
  const shadeTopY = height;
  const shadeBottomY = height - shadeH;
  const shadeAxisZ = depth - shadeTopR;
  // Backplate seated at the bottom of the box; the arm leaves its centre.
  const armY = backplateH / 2;
  const socketH = Math.min(s.socketHeight, shadeH * 0.2);
  return {
    backplateW,
    backplateH,
    backplateD,
    shadeTopR,
    shadeBottomR,
    shadeH,
    shadeTopY,
    shadeBottomY,
    shadeAxisZ,
    armY,
    armRadius: s.armRadius,
    socketH,
    bulbR: Math.min(s.bulbRadius, shadeBottomR * 0.9),
  };
}

// ---------------------------------------------------------------------------
// VANITY LIGHT BAR (`overhead-wall-light`) — downlight bell shades
// ---------------------------------------------------------------------------
// The standard 3-light DOWNLIGHT bath bar: a slim full-width backplate at
// the top of the box against the wall, a matching square rail held out
// from it on short arms, N stubs dropping from the rail, and a frosted bell
// hanging from each stub with its mouth open DOWNWARD, flush with the
// bottom of the box, and the lamp visible at the mouth. The rail sits over
// the bell axis, the bells' front rims are flush with the front of the box
// and their back rims just clear the backplate, so the fixture fills its
// declared box. Default box 24"w x 6"h x 7"d; the pipeline's fallback bar
// is 24" x 6" x 4" (`DEFAULT_DIMS.bar`).
export const VANITY_LIGHT = {
  railSize: 1 * IN_TO_M, // square rail / backplate band, clamped to the box
  railFrac: 0.2, // of height
  backplateDepth: 0.75 * IN_TO_M,
  backplateDepthFrac: 0.2, // of depth
  armRadius: 0.3 * IN_TO_M, // backplate-to-rail connector arms
  bulbPitch: 8 * IN_TO_M, // one shade per 8" of bar when no count is given
  bulbCountMin: 2,
  bulbCountMax: 6,
  bulbCountExplicitMax: 8,
  stubLength: 0.75 * IN_TO_M, // rail underside to the bell neck
  stubLengthFrac: 0.15, // of height
  stubRadius: 0.3 * IN_TO_M,
  mouthRadiusPitchFrac: 0.45, // mouth must not overlap the neighbour
  mouthRadiusBellFrac: 0.75, // of the bell length — a bell, not a dish
  neckRadiusFrac: 0.45, // of the mouth radius
  bulbRadius: 0.75 * IN_TO_M, // G16.5 globe lamp, clamped inside the bell
};

/**
 * Number of shades on a bar. An explicit `bulbCount` wins (1..8); otherwise
 * one shade per `bulbPitch` of width, 2..6. Shared by the renderer
 * (`options.bulbCount`) and the schematic (`obj.bulbCount`).
 */
export function vanityLightBulbCount(widthMeters = 0, explicit) {
  const v = VANITY_LIGHT;
  if (Number.isFinite(explicit) && explicit > 0) {
    return clamp(Math.round(explicit), 1, v.bulbCountExplicitMax);
  }
  const fromWidth = Math.round(widthMeters / v.bulbPitch);
  return clamp(fromWidth, v.bulbCountMin, v.bulbCountMax);
}

export function vanityLightProportions({ width, height, depth }, bulbCount) {
  const v = VANITY_LIGHT;
  const n = vanityLightBulbCount(width, bulbCount);
  const rail = Math.min(v.railSize, height * v.railFrac);
  const backplateD = Math.min(v.backplateDepth, depth * v.backplateDepthFrac);
  const stubL = Math.min(v.stubLength, height * v.stubLengthFrac);
  const pitch = width / n;
  const shadeXs = Array.from(
    { length: n },
    (_, i) => -width / 2 + pitch * (i + 0.5)
  );
  // Vertical stack from the top of the box: rail, stub, bell to the bottom.
  const railTopY = height;
  const railBottomY = height - rail;
  const neckY = railBottomY - stubL;
  const mouthY = 0;
  const bellLength = neckY - mouthY;
  // The mouth is the widest part: it must clear the neighbour, fit between
  // the backplate face and the front of the box, and stay bell-shaped.
  const mouthR = Math.min(
    pitch * v.mouthRadiusPitchFrac,
    (depth - backplateD) / 2,
    bellLength * v.mouthRadiusBellFrac
  );
  const neckR = mouthR * v.neckRadiusFrac;
  // Bell axis so the front rim is flush with the front of the box.
  const shadeAxisZ = depth - mouthR;
  return {
    bulbCount: n,
    rail,
    backplateD,
    armRadius: v.armRadius,
    armY: railTopY - rail / 2,
    pitch,
    shadeXs,
    railTopY,
    railBottomY,
    neckY,
    mouthY,
    bellLength,
    mouthR,
    neckR,
    stubL,
    stubRadius: v.stubRadius,
    shadeAxisZ,
    bulbR: Math.min(v.bulbRadius, mouthR * 0.6, bellLength * 0.4),
  };
}

// ---------------------------------------------------------------------------
// FLUSH-MOUNT CEILING LIGHT (`ceiling-light`) — dome diffuser
// ---------------------------------------------------------------------------
// A metal rim band against the ceiling plane and a frosted dome hanging
// below it, the classic 13"–14" flush-mount dome (rim ~3/4" tall, 4"–5"
// overall drop). The rim's outer diameter is the smaller of width/depth and
// the dome's drop is the rest of the declared height, so the fixture fills
// its box. Default box 14" x 5" x 14".
export const CEILING_LIGHT = {
  rimHeight: 0.75 * IN_TO_M,
  rimHeightFrac: 0.2, // of height, for very shallow boxes
};

export function ceilingLightProportions({ width, height, depth }) {
  const c = CEILING_LIGHT;
  const rimR = Math.min(width, depth) / 2;
  const rimH = Math.min(c.rimHeight, height * c.rimHeightFrac);
  const domeDrop = height - rimH;
  return { rimR, rimH, domeDrop };
}

// ---------------------------------------------------------------------------
// HUNG CEILING LIGHT (`ceiling-light` + `lightingSubtype` pendant/chandelier)
// ---------------------------------------------------------------------------
// A canopy at the ceiling, a stem (rod / cord / chain) down from it, and the
// shade on the end.
//
// A CATALOG pendant has no 3D twin, and that is deliberate rather than an
// omission: in the scene it is drawn from its GLB, modelled at FULL cord
// (36"-105" bboxes, median 67") and cut to length per object by shifting the
// stem band's vertices (`applyMaterialRegions`, driven by `stemLengthInches`).
// A GENERIC one (`proceduralStyle: 'pendant' | 'chandelier'`, no model) has its
// own bands (`@shared-lib/lighting/genericCeilingLights`), which this function
// reads for the elevation and `hungLightRenderer` reads for the 3D scene. An elevation
// cannot do that — its drawing of a GLB is a baked SVG silhouette, so drawing
// the projection would print a 68" fixture where a 38" one hangs. The elevation
// draws the fixture schematically instead, which is the architectural
// convention anyway: what the drawing is there to show is the HANG.
//
// **The catalog's `materialRegions` are the profile.** Each band carries its
// own vertical extent AND a `maxRadius`, so canopy / stem / shade are drawn at
// their real heights and their real widths, with nothing proportioned by this
// module. Every hung fixture in the catalog has them.
//
//   canopy_0  yMin -2.1  yMax    0   maxRadius 2.37
//   stem_1    yMin -48   yMax   -2.1 maxRadius 0.44
//   shade_2   yMin -68.1 yMax  -48   maxRadius 2.5
//
// **The stem's LENGTH is the object's, not the catalog's**, because a user
// sets it: the Ceiling Light panel writes `stemLengthInches` per placement
// (`handleApplyObjectStemLength`). It writes ONLY that field — `dimensions.y`
// keeps the length the hang planner first chose — so a drawing that derived
// the shade as `height - stem` would GROW THE SHADE by however much the cord
// was shortened. The bands make the fixture rise instead, which is what the
// 3D scene does with the same two numbers, and the object's box (whose TOP is
// the ceiling) is then simply longer than what hangs in it.
//
// Each band also reports WHERE IT IS IN THE MODEL, as a fraction of the model's
// total height (`sliceTop` / `sliceHeight`). A catalog projection's viewBox is
// the model's bounding box — verified against every hung fixture in the
// catalog, 28 of 28, canopy at the top in all of them — so those two fractions
// cut that band out of the fixture's own artwork. The canopy and the shade are
// then drawn as CROPS OF THE REAL PROJECTION, and only the stem, the part whose
// length is not the model's, is drawn as a rectangle.
//
// Without regions there is no profile to read, and the drawing falls back to a
// stem and one body rectangle sized off the object alone.
export const HUNG_LIGHT = {
  /** A body is never a bare line at the end of a stem (fallback path only). */
  minBodyHeightFrac: 0.15,
};

/** Bands top-down in the model's own frame; `yMax` 0 is the canopy face. */
function orderedBands(regions) {
  return (Array.isArray(regions) ? regions : [])
    .filter(
      (r) =>
        typeof r?.yMin === 'number' &&
        typeof r?.yMax === 'number' &&
        r.yMax > r.yMin
    )
    .slice()
    .sort((a, b) => b.yMax - a.yMax);
}

/**
 * The fixture's bands, in metres, measured DOWN from the canopy — which sits
 * at the top of the object's box, because that is where the ceiling is.
 *
 * @param {Object} p
 * @param {number} p.width  the object's width in metres (its widest band).
 * @param {number} p.height the object's box height in metres.
 * @param {number} [p.stemLengthInches] the stem the user / planner set.
 * @param {Array}  [p.regions] the model's `materialRegions`.
 * @returns {{bands: Array<{semantic: string, y: number, height: number,
 *   width: number}>, totalH: number}} `totalH` is what actually hangs, which
 *   after a cord edit is shorter (or longer) than `height`.
 */
export function hungLightProportions({
  width,
  height,
  stemLengthInches,
  regions,
}) {
  const ordered = orderedBands(regions);
  const radii = ordered
    .map((r) => r.maxRadius)
    .filter((v) => typeof v === 'number' && v > 0);
  const widest = radii.length ? Math.max(...radii) : 0;

  if (ordered.length && widest > 0) {
    // The declared width is the widest band, so every other band is drawn in
    // proportion to it — which survives a scaled placement unchanged.
    const bandWidth = (r) =>
      typeof r.maxRadius === 'number' && r.maxRadius > 0
        ? (r.maxRadius / widest) * width
        : width;
    const topY = Math.max(...ordered.map((r) => r.yMax));
    const modelH = topY - Math.min(...ordered.map((r) => r.yMin));
    let y = 0;
    const bands = ordered.map((r) => {
      const natural = (r.yMax - r.yMin) * IN_TO_M;
      const h =
        r.semantic === 'stem' && stemLengthInches > 0
          ? stemLengthInches * IN_TO_M
          : natural;
      const band = {
        semantic: r.semantic || 'body',
        y,
        height: h,
        width: bandWidth(r),
        // This band's slice of the model, top-down — and so of its artwork.
        sliceTop: modelH > 0 ? (topY - r.yMax) / modelH : 0,
        sliceHeight: modelH > 0 ? (r.yMax - r.yMin) / modelH : 0,
      };
      y += h;
      return band;
    });
    return { bands, totalH: y };
  }

  // No profile to read: a stem of the stated length and one body rectangle.
  const bodyFloor = height * HUNG_LIGHT.minBodyHeightFrac;
  const stemH =
    stemLengthInches > 0
      ? Math.max(0, Math.min(stemLengthInches * IN_TO_M, height - bodyFloor))
      : 0;
  const bands = [];
  if (stemH > 0)
    bands.push({ semantic: 'stem', y: 0, height: stemH, width: 0 });
  bands.push({
    semantic: 'body',
    y: stemH,
    height: height - stemH,
    width,
  });
  return { bands, totalH: height };
}

/**
 * How far below the ceiling a GENERIC hung light's lamp is — the middle of
 * its shade band, from the same bands the 3D scene and the elevation draw it
 * with — or null for anything else (a catalog pendant's lamps come from its
 * model). The light source goes there rather than at the box's centre, which
 * for a pendant is halfway down the cord.
 */
export function genericHungLampDrop(obj) {
  const generic = genericHungLight(obj?.proceduralStyle);
  if (!generic || obj?.modelId) return null;
  const dims = obj?.dimensions || {};
  const { bands } = hungLightProportions({
    width: dims.x || 0,
    height: dims.y || 0,
    stemLengthInches: obj?.stemLengthInches,
    regions: generic.regions,
  });
  const shade =
    bands.find((b) => b.semantic === 'shade') || bands[bands.length - 1];
  return shade ? shade.y + shade.height / 2 : null;
}

// ---------------------------------------------------------------------------
// TABLE LAMP (`staging-table-lamp`) — the bedroom kit's bedside lamp
// ---------------------------------------------------------------------------
// Proportions from the PB Director (13.75 shade, 26.5 overall): the shade is
// about 38% of the height and the base about 45%. A finial on a wire harp
// tops the shade; the bulb's socket sits a third of the way up inside it.
// Shared by the renderer (which draws it) and the lighting manager (which
// puts the bulb's light in the socket).
export const TABLE_LAMP_TYPE = 'staging-table-lamp';

/**
 * The parts a click switches the lamp by: the shade and what stands inside
 * and above it. A click on the base or the neck still selects the lamp.
 */
export const TABLE_LAMP_SWITCH_PARTS = Object.freeze(
  new Set([
    'lamp-shade',
    'lamp-socket',
    'lamp-harp',
    'lamp-spider',
    'lamp-finial',
  ])
);

/**
 * A lamp switched on: a warm 2700K bulb under a linen shade. Calibrated
 * against the scene's other point lights (`DEFAULT_POINT_LIGHT_INTENSITY_CD`
 * is 1 in this scene's units), a shaded bedside lamp a little under a
 * ceiling fixture, reaching the nightstand and the wall behind it. No shadow:
 * a shadow-casting light inside a shade is shadowed by the shade itself.
 */
export const TABLE_LAMP_LIGHT = Object.freeze({
  // A pale warm white, as a lit linen shade reads in the app.
  color: 0xffe9cc,
  intensityCd: 0.6,
  distance: 4,
  decay: 2,
  shadeGlow: 0.5, // the shade's emissive intensity while lit
});

/** The lamp's vertical stack, metres up from the box bottom. */
export function tableLampLayout({ width, height }) {
  const shadeR = width / 2;
  const shadeH = height * 0.38;
  const baseH = height * 0.45;
  const finialR = 0.45 * IN_TO_M;
  const harpTop = height - 2 * finialR;
  const shadeTop = harpTop - 0.3 * IN_TO_M;
  const shadeBottom = shadeTop - shadeH;
  const socketY = shadeBottom + shadeH * 0.35;
  return {
    shadeR,
    shadeH,
    baseH,
    belly: width * 0.24,
    finialR,
    harpTop,
    shadeTop,
    shadeBottom,
    socketY,
    // The bulb: a couple of inches above the socket, inside the shade.
    bulbY: socketY + 2.5 * IN_TO_M,
  };
}
